import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Typography
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import dayjs from 'dayjs';

import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers';
import { validateNumber } from '../../../utils/commonHelpers';

const AddEnergyRateDialog = ({ onClose, editData }) => {
    const [date, setDate] = useState(dayjs());
    const [default_val, setDefault] = useState(editData ? editData.default : false);
    const [rateName, setRateName] = useState(editData ? editData.name : '');
    const [rateData, setRateData] = useState(
        editData
            ? editData.energy_data
            : [
                  {
                      id: 1,
                      start: '00:00',
                      end: '23:59',
                      energy_cost: null,
                      co2_cost: null,
                      green_percent: null
                  }
              ]
    );
    const [loading, setLoading] = useState(false);
    const [timeError, setTimeError] = useState([]);

    const handleEndTimeChange = (newTime, id) => {
        setRateData((prevRateData) => {
            const lastIndex = id - 1;
            const newId = id + 1;

            // Update the specified duration hour's end time
            const updatedRateData = [...prevRateData];
            const startTime = dayjs(updatedRateData[lastIndex].start, 'HH:mm');

            // Format the time to only include hours and minutes
            const formattedTime = dayjs().set('hour', newTime.hour()).set('minute', newTime.minute());
            const hours = newTime?.hour().toString().padStart(2, '0');
            const minutes = newTime?.minute().toString().padStart(2, '0');
            const formatted = `${hours}:${minutes}`;

            // Check if the new end time is after the start time
            if (formattedTime.isAfter(startTime)) {
                updatedRateData[lastIndex] = {
                    ...updatedRateData[lastIndex],
                    end: formattedTime.format('HH:mm')
                };

                // Check if the end time is 23:59
                if (formattedTime.hour() === 23 && formattedTime.minute() === 59) {
                    // Find the index of the next duration hour
                    const existingIndex = updatedRateData.findIndex((d) => d.id === newId);
                    if (existingIndex !== -1) {
                        // Delete all duration hours since the existing index
                        updatedRateData.splice(existingIndex);
                    }
                } else {
                    // Check if a duration hour with newId already exists
                    const existingIndex = updatedRateData.findIndex((d) => d.id === newId);
                    if (existingIndex !== -1) {
                        // Update existing duration hour
                        updatedRateData[existingIndex] = {
                            ...updatedRateData[existingIndex],
                            start: formattedTime.format('HH:mm')
                        };
                    } else {
                        // Add new duration hour if it doesn't exist
                        updatedRateData.push({
                            id: newId,
                            start: formattedTime.format('HH:mm'),
                            end: '23:59'
                        });
                    }
                }
            } else {
                toast.error('The end time must be greater than the start time.');
                return prevRateData;
            }

            return updatedRateData;
        });
    };

    const handleEnergyChange = (e, id) => {
        if (validateNumber(e.target.value, 'number') !== true) {
            toast.error('It should be a number value.');
            return;
        }
        if (validateNumber(e.target.value, 'decimal', 4) !== true) {
            toast.error('Please enter a valid decimal number with up to 4 decimal places.');
            return;
        }
        setRateData((prevRateData) => {
            const index = id - 1;

            // Update the specified energy cost
            const updatedRateData = [...prevRateData];
            updatedRateData[index].energy_cost = e.target.value;

            return updatedRateData;
        });
    };

    const handleCo2CostChange = (e, id) => {
        if (validateNumber(e.target.value, 'number') !== true) {
            toast.error('It should be a number value.');
            return;
        }
        if (validateNumber(e.target.value, 'decimal', 3) !== true) {
            toast.error('Please enter a valid decimal number with up to 3 decimal places.');
            return;
        }
        setRateData((prevRateData) => {
            const index = id - 1;

            // Update the specified energy cost
            const updatedRateData = [...prevRateData];
            updatedRateData[index].co2_cost = e.target.value;

            return updatedRateData;
        });
    };

    const handleGreenPercentChange = (e, id) => {
        if (validateNumber(e.target.value, 'percent') !== true) {
            toast.error('It should be an number value between 0 and 100.');
            return;
        }
        if (validateNumber(e.target.value, 'decimal', 3) !== true) {
            toast.error('Please enter a valid decimal number with up to 3 decimal places.');
            return;
        }
        setRateData((prevRateData) => {
            const index = id - 1;

            // Update the specified energy cost
            const updatedRateData = [...prevRateData];
            updatedRateData[index].green_percent = e.target.value;

            return updatedRateData;
        });
    };

    const handleSubmit = () => {
        let errors = 0;
        rateData.map((row) => {
            if (!row.energy_cost || !row.co2_cost || !row.green_percent) {
                errors = errors + 1;
            }
        });
        if (rateName == '') errors++;
        if (errors == 0) {
            setLoading(true);

            ApiHelper.post(`/api/management/energy_rates/`, {
                name: rateName,
                date: date.format('YYYY-MM-DD'),
                default: default_val,
                rateData: rateData
            })
                .then((res) => {
                    if (res.status === 400) {
                        toast.success(res.data.message);
                        return;
                    }
                    toast.success('Successfully added.');
                    onClose();
                    setLoading(false);
                })
                .catch((err) => {
                    if (err.response.data.message !== '') {
                        toast.error(`Failed! ${err.response.data.message}`);
                    } else {
                        toast.error('Failed to add a new rate');
                    }
                    setLoading(false);
                });
        } else {
            toast.error('Please fill in all required fields');
        }
    };

    const handleUpdate = () => {
        let errors = 0;
        rateData.map((row) => {
            if (!row.energy_cost) {
                errors = errors + 1;
            }
        });
        if (rateName == '') errors++;
        if (errors == 0) {
            setLoading(true);

            ApiHelper.put(`/api/management/energy_rates/${editData.id}/`, {
                name: rateName,
                date: date.format('YYYY-MM-DD'),
                default: default_val,
                rateData: rateData
            })
                .then((res) => {
                    if (res.status === 400) {
                        toast.success(res.data.message);
                        return;
                    }
                    toast.success('Successfully Edited.');
                    onClose();
                    setLoading(false);
                })
                .catch((err) => {
                    if (err.response.data.message !== '') {
                        toast.error(`Failed! ${err.response.data.message}`);
                    } else {
                        toast.error('Failed to edit rate');
                    }
                    setLoading(false);
                });
        } else {
            toast.error('Please fill in all required fields');
        }
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>{editData == null ? 'Add rate' : 'Edit Rate'}</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Name"
                        value={rateName}
                        onChange={(e) => setRateName(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date"
                            dateFormat="YYYY-MM-dd"
                            value={date}
                            onChange={(newDate) => setDate(newDate)}
                            renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <FormControlLabel
                        control={<Checkbox checked={default_val} onChange={(e) => setDefault(e.target.checked)} color="primary" />}
                        label="Default"
                        sx={{ width: '100%' }}
                    />
                </Box>
                {rateData.map((row, index) => (
                    <>
                        <Box sx={{ mb: 3, mt: 3 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                    label="Start Hours"
                                    value={dayjs(row.start, 'HH:mm')}
                                    format="HH:mm"
                                    ampm={false}
                                    renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    disabled={true}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box sx={{ mb: 3, mt: 3 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                    label="End Hours"
                                    value={dayjs(row.end, 'HH:mm')}
                                    format="HH:mm"
                                    ampm={false}
                                    onChange={(newTime) => handleEndTimeChange(newTime, row.id)}
                                    renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                />
                            </LocalizationProvider>
                            {timeError && (
                                <Typography color="error" sx={{ mt: 1 }}>
                                    {timeError}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ mb: 3, mt: 3 }}>
                            <TextField
                                sx={{
                                    width: '100%'
                                }}
                                label="Energy Cost"
                                value={row.energy_cost}
                                onChange={(e) => handleEnergyChange(e, row.id)}
                            />
                        </Box>
                        <Box sx={{ mb: 3, mt: 3 }}>
                            <TextField
                                sx={{
                                    width: '100%'
                                }}
                                label={
                                    <span>
                                        CO<sub>2</sub> Cost
                                    </span>
                                }
                                value={row.co2_cost}
                                onChange={(e) => handleCo2CostChange(e, row.id)}
                            />
                        </Box>
                        <Box sx={{ mb: 3, mt: 3 }}>
                            <TextField
                                sx={{
                                    width: '100%'
                                }}
                                label="Green Percent"
                                value={row.green_percent}
                                onChange={(e) => handleGreenPercentChange(e, row.id)}
                            />
                        </Box>
                    </>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {editData ? (
                    <LoadingButton onClick={handleUpdate} loading={loading} color="primary" variant="contained">
                        <span>Update</span>
                    </LoadingButton>
                ) : (
                    <LoadingButton onClick={handleSubmit} loading={loading} color="primary" variant="contained">
                        <span>Submit</span>
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

AddEnergyRateDialog.propTypes = {
    onClose: PropTypes.func,
    editData: PropTypes.object
};

export default AddEnergyRateDialog;
